import Modal from "react-modal";
import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Receipt } from "../../../models";
import {
  deleteReceipt,
  resetDeleteReceipt,
  selectDeleteReceiptResult,
} from "../receiptsSlice";
import { ReactComponent as Close } from "../../../assets/images/close.svg";

import "./DeleteReceiptConfirmationModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

interface Props {
  // The receipt to delete, or null when the modal is closed.
  receipt: Receipt | null;
  onClose: () => void;
  // Called after a successful soft-delete so the table can refetch.
  onDeleted: () => void;
}

export const DeleteReceiptConfirmationModal = ({
  receipt,
  onClose,
  onDeleted,
}: Props) => {
  const dispatch = useAppDispatch();
  const deleteResult = useAppSelector(selectDeleteReceiptResult);

  const isOpen = receipt !== null;

  // Clear any stale delete result each time the modal opens.
  React.useEffect(() => {
    if (receipt !== null) {
      dispatch(resetDeleteReceipt());
    }
  }, [receipt, dispatch]);

  // React to the delete outcome: toast + close/refetch on success, toast on error.
  React.useEffect(() => {
    if (deleteResult.loading) return;
    if (deleteResult.error) {
      toast.error("Could not delete receipt. Please try again.");
      dispatch(resetDeleteReceipt());
    } else if (deleteResult.value) {
      toast.success("Receipt deleted.");
      dispatch(resetDeleteReceipt());
      onDeleted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!deleteResult.error, !!deleteResult.value, deleteResult.loading]);

  const confirm = () => {
    if (receipt) {
      dispatch(deleteReceipt(receipt.id));
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles}>
      <div className="delete-receipt-modal-container">
        <Close className="close-button" onClick={onClose} />

        <div className="title">Delete this receipt?</div>
        <div className="subtitle">
          It will be removed from the list. This cannot be undone here.
        </div>

        <div className="buttons-container">
          <button
            type="button"
            className="button secondary-button"
            disabled={deleteResult.loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button delete-button"
            disabled={deleteResult.loading}
            onClick={confirm}
          >
            Delete
            {deleteResult.loading && (
              <div style={{ marginLeft: 8 }}>
                <Oval
                  color="#131313"
                  height={20}
                  width={20}
                  secondaryColor="#FBD20A"
                />
              </div>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
